import { useMutation, useQueryClient } from '@tanstack/react-query';
import { publishProduct, productsQueryKeys } from '@api/products';
import { ApiError } from '@api/client';
import { toast } from '@/hooks/use-toast';
import { ToastLinkDescription } from '@/components/ui/toast';
import { useTranslation } from '../useTranslation';

export interface PublishProductInput {
  id: number;
  name: string;
}

interface UsePublishProductOptions {
  /** When false, the 422 error toast renders as plain text instead of linking to
   *  the edit page. Use on the edit page itself, where linking back would be
   *  circular. */
  linkToEditOn422?: boolean;
}

export function usePublishProduct(options?: UsePublishProductOptions) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { linkToEditOn422 = true } = options || {};

  return useMutation({
    mutationFn: ({ id }: PublishProductInput) => publishProduct(id),
    onSuccess: (data) => {
      const product = data.product;
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.detail(product.id) });

      const { dismiss } = toast({
        description: (
          <ToastLinkDescription
            name={product.name}
            href={`/products/${product.id}/edit`}
            label={t('publish.toast.success')}
            onNavigate={() => dismiss()}
          />
        ),
      });
    },
    onError: (error, variables) => {
      if (error instanceof ApiError && error.status === 422) {
        if (linkToEditOn422) {
          const { dismiss } = toast({
            description: (
              <ToastLinkDescription
                name={variables.name}
                href={`/products/${variables.id}/edit`}
                label={t('publish.toast.error_422')}
                onNavigate={() => dismiss()}
              />
            ),
            variant: 'destructive',
          });
          return;
        }

        toast({
          title: t('publish.toast.error'),
          description: `${variables.name} ${t('publish.toast.error_422')}`,
          variant: 'destructive',
        });
        return;
      }

      const description = error instanceof ApiError && error.status < 500
        ? error.message
        : t('publish.toast.error_generic');

      toast({
        title: t('publish.toast.error'),
        description,
        variant: 'destructive',
      });
    },
  });
}
